import {
  AlertCircle,
  AlertTriangle,
  Building2,
  CalendarClock,
  ChevronRight,
  History,
  ListTree,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Route,
  Undo2,
  Wallet,
} from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaDropdownMenu, type ColocaDropdownItem } from "@/shared/components/overlays/ColocaDropdownMenu";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatTaxLabel } from "@/shared/utils/paymentTaxes";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import {
  EconomicModel,
  PaymentFeeKind,
  type CorridorCoverage,
  type PaymentFee,
} from "../../types/paymentFees.types";
import { corridorLabel, formatFeeFormula, shortId } from "../../utils/feeFormat";
import { CorridorIdentity } from "./CorridorIdentity";

const COVERAGE_PILL: Record<CorridorCoverage["status"], "success" | "warning" | "danger"> = {
  PRICED: "success",
  PARTIAL: "warning",
  UNPRICED: "danger",
};

export interface CorridorActions {
  onEditCommission: (corridor: CorridorCoverage) => void;
  onEditCost: (corridor: CorridorCoverage) => void;
  onBackToGeneral: (corridor: CorridorCoverage) => void;
  onHistory: (corridor: CorridorCoverage) => void;
  onViewRules: (corridor: CorridorCoverage, kind: PaymentFeeKind) => void;
  onViewOrganization: (organizationId: string) => void;
}

export interface CorridorCoverageSectionProps extends CorridorActions {
  corridors: CorridorCoverage[];
  /** Corridors before the KPI filter — tells an empty filter apart from an empty catalog. */
  totalCount: number;
  isLoading: boolean;
  routesError: boolean;
  onRetryRoutes: () => void;
  /** `null` is the general price. */
  scopeOrganizationId: string | null;
  organizationNames: Record<string, string>;
}

function isNegative(margin: string | null): boolean {
  return margin != null && compareDecimalStrings(margin, "0") < 0;
}

function marginTone(margin: string | null): string {
  if (margin == null) return "text-warning";
  const sign = compareDecimalStrings(margin, "0");
  if (sign < 0) return "text-destructive";
  if (sign === 0) return "text-muted-foreground";
  return "text-success";
}

/**
 * One row per corridor, read through the page scope: the general price, or what one organization actually
 * pays. Below `lg` the same rows render as cards — the table carries more columns than a phone can show.
 */
export function CorridorCoverageSection({
  corridors,
  totalCount,
  isLoading,
  routesError,
  onRetryRoutes,
  scopeOrganizationId,
  organizationNames,
  ...actions
}: CorridorCoverageSectionProps) {
  const { t } = useTranslations("paymentFees");
  const scoped = scopeOrganizationId != null;

  const columns = useMemo<ColocaDataTableColumn<CorridorCoverage>[]>(
    () => [
      {
        id: "corridor",
        header: t("corridors_th_corridor"),
        cellClassName: "relative",
        cell: (corridor) => <CorridorCell corridor={corridor} />,
      },
      {
        id: "commission",
        header: t(scoped ? "corridors_th_commission_org" : "corridors_th_commission_general"),
        cell: (corridor) => <CommissionCell corridor={corridor} scoped={scoped} />,
      },
      ...(scoped
        ? []
        : [
            {
              id: "special",
              header: t("corridors_th_special"),
              cell: (corridor: CorridorCoverage) => (
                <SpecialPriceCell
                  corridor={corridor}
                  organizationNames={organizationNames}
                  onViewOrganization={actions.onViewOrganization}
                />
              ),
            } satisfies ColocaDataTableColumn<CorridorCoverage>,
          ]),
      {
        id: "cost",
        header: t("corridors_th_cost"),
        cell: (corridor) => (
          <AmountWithFormula
            fee={corridor.cost}
            amount={corridor.costAmount}
            currency={corridor.route.currency}
            missingLabel={t("missing_cost")}
          />
        ),
      },
      {
        id: "client",
        header: t("corridors_th_client"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (corridor) => <ClientPaysCell corridor={corridor} />,
      },
      {
        id: "margin",
        header: t("corridors_th_margin"),
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (corridor) => <MarginCell corridor={corridor} />,
      },
      {
        id: "status",
        header: t("corridors_th_status"),
        cell: (corridor) => <StatusCell corridor={corridor} />,
      },
      {
        id: "actions",
        header: "",
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (corridor) => <RowActions corridor={corridor} scoped={scoped} actions={actions} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      scoped,
      organizationNames,
      actions.onEditCommission,
      actions.onEditCost,
      actions.onBackToGeneral,
      actions.onHistory,
      actions.onViewRules,
      actions.onViewOrganization,
    ],
  );

  if (routesError) {
    return (
      <ColocaAlert
        variant="destructive"
        icon={AlertCircle}
        heading={t("routes_error")}
        text={t("routes_error_detail")}
        actions={
          <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={onRetryRoutes}>
            {t("error_retry")}
          </ColocaButton>
        }
      />
    );
  }

  const empty =
    totalCount > 0 ? (
      <ColocaEmpty
        icon={Route}
        title={t("corridors_filtered_empty_title")}
        description={t("corridors_filtered_empty_description")}
        surface="card"
      />
    ) : (
      <ColocaEmpty
        icon={Route}
        title={t("corridors_empty_title")}
        description={t("corridors_empty_description")}
        surface="card"
      />
    );

  return (
    <div className="space-y-4">
      <div className="hidden lg:block">
        <ColocaDataTable
          columns={columns}
          rows={corridors}
          rowKey={(corridor) => corridor.route.id}
          loading={isLoading}
          skeletonRows={6}
          minWidth={scoped ? 1080 : 1220}
          rowClassName={(corridor) => (isNegative(corridor.margin) ? "bg-destructive/[0.03]" : undefined)}
          empty={empty}
        />
      </div>
      <div className="lg:hidden">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="h-48 animate-pulse rounded-xl border border-border bg-muted" />
            ))}
          </div>
        ) : corridors.length === 0 ? (
          empty
        ) : (
          <div className="flex flex-col gap-3">
            {corridors.map((corridor) => (
              <CorridorCard
                key={corridor.route.id}
                corridor={corridor}
                scoped={scoped}
                organizationNames={organizationNames}
                actions={actions}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CorridorCell({ corridor }: { corridor: CorridorCoverage }) {
  const labels = useFeeLabels();
  const { route } = corridor;
  const absorbed = route.economic_model === EconomicModel.ABSORBED;
  return (
    <>
      {isNegative(corridor.margin) && (
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-0.5 bg-destructive" />
      )}
      <CorridorIdentity
        route={route}
        layout="stacked"
        note={absorbed ? labels.model[EconomicModel.ABSORBED] : undefined}
      />
    </>
  );
}

function formulaSubline(
  fee: PaymentFee | null,
  tierIndex: number | null | undefined,
  locale: string,
  labels: ReturnType<typeof useFeeLabels>,
  tierLabel: (tier: number) => string,
): string {
  if (!fee) return "";
  const formula = formatFeeFormula(fee, locale, labels.formula);
  return tierIndex ? `${formula} · ${tierLabel(tierIndex)}` : formula;
}

function AmountWithFormula({
  fee,
  amount,
  currency,
  missingLabel,
  tierIndex,
}: {
  fee: PaymentFee | null;
  amount: string | null;
  currency: string;
  missingLabel: string;
  tierIndex?: number | null;
}) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  if (amount == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
        <AlertTriangle className="size-3.5" aria-hidden="true" />
        {missingLabel}
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5">
      <ColocaAmountDisplay
        value={amount}
        currency={currency}
        size="sm"
        minimumFractionDigits={0}
        maximumFractionDigits={8}
      />
      <span className="max-w-60 truncate text-[11px] text-muted-foreground">
        {formulaSubline(fee, tierIndex, activeLanguage, labels, (tier) => t("tier_applied", { tier }))}
      </span>
    </span>
  );
}

function CommissionCell({ corridor, scoped }: { corridor: CorridorCoverage; scoped: boolean }) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const isSpecial = corridor.commissionSource === "ORGANIZATION";

  return (
    <span className="flex flex-col gap-1">
      <AmountWithFormula
        fee={corridor.commission}
        amount={corridor.commissionAmount}
        currency={corridor.route.currency}
        missingLabel={t("missing_commission")}
        tierIndex={corridor.commissionComputation?.tierIndex}
      />
      {scoped && corridor.commission && (
        <span className="flex flex-wrap items-center gap-1.5">
          <ColocaStatusPill
            variant={isSpecial ? "info" : "neutral"}
            size="sm"
            icon={isSpecial ? Building2 : undefined}
          >
            {t(isSpecial ? "source_special" : "source_general")}
          </ColocaStatusPill>
          {isSpecial && (
            <span className="text-[11px] text-muted-foreground">
              {corridor.globalCommission
                ? t("source_general_baseline", {
                    price: formatFeeFormula(corridor.globalCommission, activeLanguage, labels.formula),
                  })
                : t("source_no_general")}
            </span>
          )}
        </span>
      )}
      {scoped && corridor.ownRuleShadowed && (
        <ColocaTooltip
          triggerRender={
            <span className="inline-flex cursor-help items-center gap-1 text-[11px] font-medium text-warning">
              <AlertTriangle className="size-3" aria-hidden="true" />
              {t("source_special")}
            </span>
          }
        >
          {t("shadowed_warning")}
        </ColocaTooltip>
      )}
    </span>
  );
}

function SpecialPriceCell({
  corridor,
  organizationNames,
  onViewOrganization,
}: {
  corridor: CorridorCoverage;
  organizationNames: Record<string, string>;
  onViewOrganization: (organizationId: string) => void;
}) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  if (corridor.orgOverrides.length === 0) {
    return <span className="text-xs text-muted-foreground">{t("special_none")}</span>;
  }
  return (
    <ColocaPopover
      align="start"
      contentClassName="w-96"
      title={t("special_popover_title")}
      description={t("special_popover_hint")}
      trigger={
        <ColocaButton variant="secondary" size="xs" icon={Building2}>
          {t("special_count", { count: corridor.orgOverrides.length })}
        </ColocaButton>
      }
    >
      <div className="flex flex-col gap-1">
        {corridor.orgOverrides.map((override) => (
          <ColocaButton
            key={override.organizationId}
            variant="ghost"
            size="sm"
            className="h-auto w-full justify-between gap-3 py-1.5"
            onClick={() => onViewOrganization(override.organizationId)}
          >
            <span className="flex min-w-0 flex-col items-start">
              <span className="max-w-full truncate">
                {organizationNames[override.organizationId] ?? shortId(override.organizationId)}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {formatFeeFormula(override.fee, activeLanguage, labels.formula)}
              </span>
            </span>
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          </ColocaButton>
        ))}
      </div>
    </ColocaPopover>
  );
}

function MoneyOrDash({ value, currency }: { value: string | null; currency: string }) {
  if (value == null) return <span className="text-warning">—</span>;
  return (
    <ColocaAmountDisplay
      value={value}
      currency={currency}
      size="sm"
      minimumFractionDigits={0}
      maximumFractionDigits={8}
    />
  );
}

function ClientPaysCell({ corridor }: { corridor: CorridorCoverage }) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const taxes = corridor.taxes.filter((line) => line.tax.bearer === "CLIENT");
  return (
    <span className="inline-flex flex-col items-end">
      <MoneyOrDash value={corridor.clientPays} currency={corridor.route.currency} />
      {taxes.length > 0 && corridor.clientPays != null && (
        <span className="text-[11px] text-muted-foreground">
          {t("client_pays_taxes", {
            taxes: taxes.map((line) => formatTaxLabel(line.tax, activeLanguage)).join(" · "),
          })}
        </span>
      )}
    </span>
  );
}

function MarginCell({ corridor }: { corridor: CorridorCoverage }) {
  const { t } = useTranslations("paymentFees");
  const value =
    corridor.margin == null ? (
      <span className="text-warning">—</span>
    ) : (
      <ColocaAmountDisplay
        value={corridor.margin}
        currency={corridor.route.currency}
        size="sm"
        signed
        minimumFractionDigits={0}
        maximumFractionDigits={8}
        className={marginTone(corridor.margin)}
      />
    );
  if (!isNegative(corridor.margin)) return value;
  return (
    <ColocaTooltip triggerRender={<span className="cursor-help">{value}</span>}>
      {t("negative_margin_tooltip")}
    </ColocaTooltip>
  );
}

function StatusCell({ corridor }: { corridor: CorridorCoverage }) {
  const { t } = useTranslations("paymentFees");
  return (
    <span className="inline-flex items-center gap-1.5">
      <ColocaStatusPill variant={COVERAGE_PILL[corridor.status]} size="sm">
        {t(
          corridor.status === "PRICED"
            ? "coverage_priced"
            : corridor.status === "PARTIAL"
              ? "coverage_partial"
              : "coverage_unpriced",
        )}
      </ColocaStatusPill>
      {corridor.hasScheduledChange && (
        <ColocaTooltip
          triggerRender={
            <span className="inline-flex cursor-help text-blue-700 dark:text-blue-300">
              <CalendarClock className="size-3.5" aria-label={t("scheduled_chip")} />
            </span>
          }
        >
          {t("scheduled_chip")}
        </ColocaTooltip>
      )}
    </span>
  );
}

function useRowMenu(
  corridor: CorridorCoverage,
  scoped: boolean,
  actions: CorridorActions,
): ColocaDropdownItem[] {
  const { t } = useTranslations("paymentFees");
  const items: ColocaDropdownItem[] = [];
  if (scoped && corridor.commissionSource === "ORGANIZATION") {
    items.push({
      label: t("action_back_to_general"),
      icon: Undo2,
      onClick: () => actions.onBackToGeneral(corridor),
    });
  }
  if (!scoped) {
    items.push({ label: t("action_edit_cost"), icon: Wallet, onClick: () => actions.onEditCost(corridor) });
  }
  items.push({
    label: t("action_view_rules"),
    icon: ListTree,
    onClick: () => actions.onViewRules(corridor, PaymentFeeKind.COLOCA_COMMISSION),
  });
  return items;
}

function primaryLabelKey(corridor: CorridorCoverage, scoped: boolean) {
  if (!scoped) return "action_edit_commission" as const;
  return corridor.commissionSource === "ORGANIZATION"
    ? ("action_change_special" as const)
    : ("action_give_special" as const);
}

function RowActions({
  corridor,
  scoped,
  actions,
}: {
  corridor: CorridorCoverage;
  scoped: boolean;
  actions: CorridorActions;
}) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const menu = useRowMenu(corridor, scoped, actions);
  return (
    <span className="inline-flex items-center justify-end gap-1">
      <ColocaButton
        variant="secondary"
        size="xs"
        icon={Pencil}
        onClick={() => actions.onEditCommission(corridor)}
      >
        {t(primaryLabelKey(corridor, scoped))}
      </ColocaButton>
      <ColocaTooltip
        triggerRender={
          <ColocaIconButton
            icon={History}
            variant="ghost"
            size="sm"
            aria-label={t("action_history")}
            onClick={() => actions.onHistory(corridor)}
          />
        }
      >
        {t("action_history")}
      </ColocaTooltip>
      <ColocaDropdownMenu
        align="end"
        items={menu}
        trigger={
          <ColocaIconButton
            icon={MoreHorizontal}
            variant="ghost"
            size="sm"
            aria-label={t("action_more_aria", {
              corridor: corridorLabel(corridor.route, labels.directionShort),
            })}
          />
        }
      />
    </span>
  );
}

function CorridorCard({
  corridor,
  scoped,
  organizationNames,
  actions,
}: {
  corridor: CorridorCoverage;
  scoped: boolean;
  organizationNames: Record<string, string>;
  actions: CorridorActions;
}) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const menu = useRowMenu(corridor, scoped, actions);

  return (
    <article
      className={cn(
        "space-y-3 rounded-xl border border-border bg-card p-4",
        isNegative(corridor.margin) && "border-l-2 border-l-destructive",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <CorridorCell corridor={corridor} />
        <StatusCell corridor={corridor} />
      </div>

      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 text-sm">
        <dt className="text-muted-foreground">
          {t(scoped ? "corridors_th_commission_org" : "corridors_th_commission_general")}
        </dt>
        <dd className="text-right">
          <CommissionCell corridor={corridor} scoped={scoped} />
        </dd>
        {!scoped && (
          <>
            <dt className="text-muted-foreground">{t("corridors_th_special")}</dt>
            <dd className="text-right">
              <SpecialPriceCell
                corridor={corridor}
                organizationNames={organizationNames}
                onViewOrganization={actions.onViewOrganization}
              />
            </dd>
          </>
        )}
        <dt className="text-muted-foreground">{t("corridors_th_cost")}</dt>
        <dd className="text-right">
          <AmountWithFormula
            fee={corridor.cost}
            amount={corridor.costAmount}
            currency={corridor.route.currency}
            missingLabel={t("missing_cost")}
          />
        </dd>
        <dt className="text-muted-foreground">{t("corridors_th_client")}</dt>
        <dd className="text-right">
          <ClientPaysCell corridor={corridor} />
        </dd>
        <dt className="text-muted-foreground">{t("corridors_th_margin")}</dt>
        <dd className="text-right">
          <MarginCell corridor={corridor} />
        </dd>
      </dl>

      {isNegative(corridor.margin) && (
        <p className="text-xs text-destructive">{t("negative_margin_tooltip")}</p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <ColocaButton
          variant="secondary"
          size="sm"
          icon={Pencil}
          onClick={() => actions.onEditCommission(corridor)}
        >
          {t(primaryLabelKey(corridor, scoped))}
        </ColocaButton>
        <ColocaButton variant="ghost" size="sm" icon={History} onClick={() => actions.onHistory(corridor)}>
          {t("action_history")}
        </ColocaButton>
        <span className="ml-auto">
          <ColocaDropdownMenu
            align="end"
            items={menu}
            trigger={
              <ColocaIconButton
                icon={MoreHorizontal}
                variant="ghost"
                size="sm"
                aria-label={t("action_more_aria", {
                  corridor: corridorLabel(corridor.route, labels.directionShort),
                })}
              />
            }
          />
        </span>
      </div>
    </article>
  );
}
